// Portals-4 (2026-06-21) — Xero-style top-right company switcher for the
// Client Portal. Lists every entity the current client has access to
// (GET /api/me/entities), surfaces the active one in localStorage at
// `solara.client.activeEntityId`, and lets the user create a new entity
// inline via POST /api/client-entities.
//
// If the client has only one entity we render the name as plain text
// (no chevron, no dropdown chrome) — the switcher only exists when
// there's something to switch between or a "+ Add new company" affordance
// to surface. The "+ Add" affordance is always available even with a
// single entity, so the trigger stays visible as a small icon button
// when there's only one row.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, ChevronDown, Check, Plus, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY = "solara.client.activeEntityId";

export function getActiveEntityId() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function setActiveEntityId(id) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage unavailable — safe to ignore, server still works */
  }
}

export default function EntitySwitcher({
  entities,              // [{id, entity_name, my_role, ...}]
  activeEntityId,
  onChange,              // (newEntityId) => void
  onCreated,             // (newEntity) => void — parent appends + selects
}) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("Company");
  const wrapRef = useRef(null);

  // Close the dropdown on outside-click. Stays open while the create form
  // is showing so the user can type without losing focus.
  useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current) return;
      if (wrapRef.current.contains(e.target)) return;
      setOpen(false);
      setShowCreate(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const active = (entities || []).find((e) => e.id === activeEntityId) || (entities || [])[0];
  const activeName = active?.entity_name || "Select company";

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Company name is required");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/client-entities`, {
        entity_name: name,
        entity_type: newType,
      });
      const created = res.data?.entity;
      if (created) {
        toast.success(`${created.entity_name} added`);
        setActiveEntityId(created.id);
        onCreated?.(created);
        setShowCreate(false);
        setOpen(false);
        setNewName("");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add company");
    } finally {
      setCreating(false);
    }
  };

  // Single-entity case: still render a small trigger so the user can add
  // a second company. The trigger collapses to icon + name only (no chevron)
  // when there's nothing to switch between.
  const onlyOne = (entities || []).length <= 1;

  return (
    <div ref={wrapRef} className="relative" data-testid="entity-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-white/90 hover:text-white hover:bg-white/10 text-sm transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="entity-switcher-trigger"
      >
        <Building2 className="w-4 h-4 text-[#bd9a59]" />
        <span className="font-medium truncate max-w-[180px]">{activeName}</span>
        {!onlyOne && <ChevronDown className="w-3.5 h-3.5 opacity-70" />}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-40 overflow-hidden"
          role="menu"
        >
          <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-400 border-b">
            Your companies
          </div>
          <div className="max-h-72 overflow-y-auto">
            {(entities || []).map((e) => {
              const isActive = e.id === active?.id;
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => {
                    setActiveEntityId(e.id);
                    onChange?.(e.id);
                    setOpen(false);
                  }}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${isActive ? "bg-gray-50" : ""}`}
                  data-testid={`entity-switcher-row-${e.id}`}
                >
                  <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="flex-1 truncate text-[#021d49]">{e.entity_name}</span>
                  {isActive && <Check className="w-4 h-4 text-[#bd9a59]" />}
                </button>
              );
            })}
            {(!entities || entities.length === 0) && (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                No companies yet — add one to get started.
              </div>
            )}
          </div>

          {!showCreate ? (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="w-full flex items-center gap-2 px-3 py-2.5 border-t text-sm text-[#021d49] hover:bg-gray-50"
              data-testid="entity-switcher-add"
            >
              <Plus className="w-4 h-4 text-[#bd9a59]" />
              Add new company
            </button>
          ) : (
            <div className="border-t p-3 space-y-2 bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-[#021d49] uppercase tracking-wide">New company</span>
                <button
                  type="button"
                  onClick={() => { setShowCreate(false); setNewName(""); }}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close add form"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Company / trust / SMSF name"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#bd9a59]"
                autoFocus
                data-testid="entity-switcher-new-name"
              />
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
                data-testid="entity-switcher-new-type"
              >
                <option>Company</option>
                <option>Trust</option>
                <option>SMSF</option>
                <option>Individual</option>
                <option>Other</option>
              </select>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating || !newName.trim()}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#021d49] text-white text-sm rounded hover:bg-[#052b66] disabled:opacity-50"
                data-testid="entity-switcher-create"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Create
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
